import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import PageSEO from '@/components/base/PageSEO';

const milestones = [
  { year: '2018', title: '平台创立', description: '木瓜玩在新加坡成立，聚焦东南亚游戏发行服务' },
  { year: '2019', title: '业务扩展', description: '新增广告投放优化业务，服务区域扩展至日韩市场' },
  { year: '2020', title: '技术升级', description: '自研广告归因引擎上线，实现全链路数据追踪' },
  { year: '2021', title: '全球化布局', description: '设立迪拜、旧金山办公室，覆盖欧美及中东市场' },
  { year: '2022', title: '规模突破', description: '累计服务500+游戏产品，年广告投放额突破10亿' },
  { year: '2024', title: 'AI赋能', description: '推出AI驱动的智能投放系统，ROI平均提升3.2倍' },
  { year: '2026', title: '生态完善', description: '构建创意素材、数据分析、用户运营一体化增长生态' },
];

const faqData = [
  { q: '木瓜玩支持哪些广告平台的投放？', a: '我们全面支持Meta Ads（Facebook/Instagram）、Google Ads（含UAC）、TikTok Ads、Unity Ads、Apple Search Ads、X Ads、Snapchat Ads等全球主流广告平台，以及程序化DSP渠道。' },
  { q: '合作模式是怎样的？', a: '我们提供灵活的收费模式：纯服务费模式（按广告消耗比例收取）、效果对赌模式（按CPA/CPS结算）和混合模式。具体模式根据客户需求和项目阶段协商确定。' },
  { q: '数据分析平台的数据安全如何保障？', a: '我们通过了SOC 2 Type II认证和GDPR合规审查。所有数据传输采用TLS 1.3加密，客户数据严格隔离存储，提供完善的数据导出和删除选项。' },
  { q: '创意素材制作的交付周期是多久？', a: '标准视频素材3-5个工作日，可玩广告7-10个工作日，紧急需求可加急至48小时内交付。我们配备专属创意团队，支持每周2-3次迭代优化。' },
  { q: '如何开始与木瓜玩的合作？', a: '您可以通过本页面底部的联系表单提交需求，我们的商务团队会在24小时内与您联系，安排免费的增长诊断和方案演示。合作启动周期通常为1-2周。' },
];

const teamMembers = [
  { name: '张磊', role: '创始人 & CEO', bio: '前Google亚太游戏业务负责人，15年游戏出海经验' },
  { name: '陈思远', role: '联合创始人 & CTO', bio: '前字节跳动广告算法专家，AI与大数据领域资深工程师' },
  { name: '李明珠', role: 'COO', bio: '前AppLovin大中华区运营总监，精通全球买量运营' },
  { name: '赵雪', role: '创意副总裁', bio: '前Zynga首席创意设计师，管理超过200人创意团队' },
];

const aboutImage = 'https://readdy.ai/api/search-image?query=Modern%20tech%20office%20with%20diverse%20international%20team%20collaborating%20around%20large%20monitors%20displaying%20game%20analytics%20dashboards%2C%20warm%20natural%20lighting%2C%20professional%20corporate%20environment%2C%20creative%20workspace%20with%20gaming%20elements%2C%20clean%20modern%20design%2C%20editorial%20photography%20style&width=1200&height=560&seq=about-muguawan-team&orientation=landscape';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeyField = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeyField && honeyField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('idle');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9sm9i58o1vn02ekuhvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg.includes('spam') ? '提交失败，请检查输入内容。' : (serverMsg || '提交失败，请稍后重试。'));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试或直接发送邮件至 contact@muguawan.com');
    }
  };

  return (
    <>
      <PageSEO
        title="关于木瓜玩 - 海外游戏发行与用户增长平台 | 品牌介绍"
        description="了解木瓜玩品牌故事、团队背景与核心优势。专注海外游戏发行、买量投放与用户增长，服务全球30+市场，助力500+游戏产品成功出海。"
        keywords="木瓜玩品牌,木瓜玩团队,游戏发行公司,出海营销平台,海外广告投放"
        canonicalUrl="https://steamvip520.cn/about"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于木瓜玩",
          "description": "木瓜玩是专注海外游戏发行、买量投放与用户增长的一站式营销平台",
          "url": "https://steamvip520.cn/about",
          "isPartOf": {
            "@type": "WebSite",
            "name": "木瓜玩",
            "url": "https://steamvip520.cn/"
          }
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="px-6 md:px-10 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mt-4 mb-4">
              关于木瓜玩
            </h1>
            <p className="text-sm md:text-lg text-foreground-600 leading-relaxed max-w-3xl">
              我们是一支由游戏行业资深从业者和AI技术专家组成的团队，致力于通过技术与创意赋能游戏出海企业，让每一款优秀的游戏都能在全球市场找到属于它的玩家。
            </p>
          </div>
        </div>

        <section className="px-6 md:px-10 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3">
                  品牌故事
                </span>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                  让游戏出海更简单、更高效
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    木瓜玩成立于2018年，总部位于新加坡。创始团队来自Google、Meta、字节跳动等全球顶尖科技公司，拥有超过15年的游戏发行和用户增长经验。
                  </p>
                  <p>
                    我们洞察到游戏出海企业面临的核心挑战——如何在全球多元化的市场中精准获客、高效转化。于是，木瓜玩应运而生：将<strong>数据技术与创意能力深度融合</strong>，打造一站式的游戏出海增长平台。
                  </p>
                  <p>
                    截至2026年，木瓜玩已服务<strong>超过500款游戏产品</strong>，覆盖东南亚、日韩、欧美、中东等30+国家和地区，累计管理广告预算超过50亿人民币，帮助客户实现平均<strong>3.2倍的ROI提升</strong>。
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-8">
                  {[
                    { value: '500+', label: '服务游戏' },
                    { value: '30+', label: '覆盖国家' },
                    { value: '3.2倍', label: '平均ROI' },
                  ].map((item) => (
                    <div key={item.label} className="text-center p-3 bg-background-100 rounded-lg">
                      <div className="font-heading font-bold text-lg md:text-xl text-primary-600">{item.value}</div>
                      <div className="text-xs text-foreground-500 mt-1">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg overflow-hidden">
                <img
                  src={aboutImage}
                  alt="木瓜玩国际化团队协作"
                  title="木瓜玩团队 - 海外游戏发行专家"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 md:px-10 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
                核心团队
              </span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                行业顶尖专家领衔
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {teamMembers.map((member) => (
                <div key={member.name} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-secondary-200 mx-auto mb-4 flex items-center justify-center">
                    <i className="ri-user-3-line text-2xl text-secondary-600"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground-950">{member.name}</h3>
                  <p className="text-xs text-primary-600 font-medium mt-1">{member.role}</p>
                  <p className="text-xs text-foreground-500 mt-2 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 md:px-10 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2"></div>
              <div className="space-y-8">
                {milestones.map((item, idx) => (
                  <div key={item.year} className={`flex flex-col lg:flex-row items-start gap-4 lg:gap-8 ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                      <div className="lg:hidden text-sm font-bold text-primary-500 mb-1">{item.year}</div>
                      <h3 className="font-heading font-semibold text-base text-foreground-950">{item.title}</h3>
                      <p className="text-xs md:text-sm text-foreground-500 mt-1">{item.description}</p>
                    </div>
                    <div className="hidden lg:flex items-center justify-center shrink-0">
                      <div className="w-3 h-3 rounded-full bg-primary-500 ring-4 ring-primary-100"></div>
                    </div>
                    <div className="flex-1 hidden lg:block">
                      {idx % 2 === 0 ? (
                        <span className="font-heading font-bold text-lg text-primary-500">{item.year}</span>
                      ) : null}
                    </div>
                    <div className={`flex-1 hidden lg:block ${idx % 2 !== 0 ? '' : ''}`}>
                      {idx % 2 !== 0 ? (
                        <span className="font-heading font-bold text-lg text-primary-500">{item.year}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className="px-6 md:px-10 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-3">
                常见问题
              </span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                了解更多
              </h2>
            </div>
            <div className="space-y-3">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group border border-background-200/70 rounded-lg overflow-hidden bg-background-50">
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 text-base shrink-0"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqData.map((item) => ({
                  "@type": "Question",
                  "name": item.q,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.a
                  }
                }))
              })
            }}
          />
        </section>

        <section id="contact" className="px-6 md:px-10 py-10 md:py-14 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3">
                联系我们
              </span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">
                开启全球增长之旅
              </h2>
              <p className="text-sm text-foreground-500">
                填写下方表单，我们的商务团队将在24小时内与您联系
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    公司名称
                  </label>
                  <input
                    id="contact-company"
                    name="company"
                    type="text"
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入公司名称"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入工作邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    手机号
                  </label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入手机号"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-service" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  感兴趣的服务
                </label>
                <select
                  id="contact-service"
                  name="service"
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  <option value="">请选择</option>
                  <option value="advertising">精准广告投放</option>
                  <option value="analytics">数据分析与归因</option>
                  <option value="creative">创意素材制作</option>
                  <option value="growth">用户增长运营</option>
                  <option value="consulting">出海战略咨询</option>
                  <option value="other">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  需求描述 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请描述您的游戏产品或业务需求（限500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
              </div>

              <input type="text" name="company_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

              {formStatus === 'success' && (
                <div className="p-4 bg-accent-100 text-accent-700 text-sm rounded-md">
                  提交成功！我们将在24小时内与您联系。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 bg-red-100 text-red-700 text-sm rounded-md">
                  {formError || '提交失败，请稍后重试或直接发送邮件至 contact@muguawan.com'}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                提交咨询
              </button>
            </form>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="p-4">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">商务合作</p>
                <p className="text-sm font-medium text-foreground-900">contact@muguawan.com</p>
              </div>
              <div className="p-4">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-accent-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">咨询热线</p>
                <p className="text-sm font-medium text-foreground-900">400-000-0000</p>
              </div>
              <div className="p-4">
                <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-line text-secondary-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">总部地址</p>
                <p className="text-sm font-medium text-foreground-900">新加坡莱佛士坊</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}